/**
 * B14: the family announcements a STAFF PARENT reads for her own dancers.
 *
 * Bobbie, 2026-09-09 (DECISIONS.md entry 28, item 1): a staff sign-in linked
 * to a dancer, through the guardian link or the Jr Staff dancer link, reads
 * the family-only announcements that dancer's family reads. The rules cannot
 * see that link on a list query, so the browser asks the server lane
 * netlify/functions/staffParentBulletins.mjs, which proves it with the Admin
 * SDK (DECISIONS 108, server first; zero rules bytes).
 *
 * Who asks: an ACTIVE account on manager, company (Executive Teacher),
 * ensemble (Jr Staff) or foh (Admin). A family account already reads family
 * announcements by role, and the Director reads every announcement, so
 * neither ever calls the lane. A staff member with no dancer link pays one
 * small call per session and is answered "not linked".
 *
 * Each row comes back marked `viaDancerLink: true`, a browser-only flag that
 * is never written to Firestore. It is what lets lib/bulletins.ts treat the
 * row as hers (the Got it button, the home banner count, Messages' Done)
 * and send her Got it through the lane, since the rules refuse her own
 * seen-marker write on a family announcement.
 *
 * One answer is shared by every screen for five minutes (module cache keyed
 * by uid and depth), so a home screen that mounts the announcement hook in
 * three places makes one call, not three.
 */
import type { User } from 'firebase/auth';
import { Timestamp } from 'firebase/firestore';
import { functionHeaders } from './functionSecurity';
import type { UserRole } from './userRole';
import { lateJoinMayRetry } from './bulletinLateRecipient';

export const STAFF_PARENT_BULLETINS_ENDPOINT = '/.netlify/functions/staffParentBulletins';
/** Fired after a Got it through the lane, so every open list asks again. */
export const STAFF_PARENT_BULLETINS_CHANGED_EVENT = 'ldg:staff-parent-bulletins-changed';
/** How long one answer is shared before a newly mounted list asks again. */
export const STAFF_PARENT_BULLETINS_FRESH_MS = 5 * 60 * 1000;

const ASKING_ROLES: readonly UserRole[] = ['manager', 'company', 'ensemble', 'foh'];

/** Only an active staff account other than the Director asks the lane. */
export function asksForDancerLinkedBulletins(
  userDoc: { role?: UserRole | null; status?: string | null } | null | undefined
): boolean {
  return !!userDoc
    && userDoc.status === 'active'
    && !!userDoc.role
    && ASKING_ROLES.includes(userDoc.role);
}

/** { __ldgTs: ms } back into a Timestamp, everywhere in a row. */
function revive(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(revive);
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const keys = Object.keys(record);
    if (keys.length === 1 && keys[0] === '__ldgTs' && typeof record.__ldgTs === 'number'
        && Number.isFinite(record.__ldgTs)) {
      return Timestamp.fromMillis(record.__ldgTs);
    }
    const out: Record<string, unknown> = {};
    for (const key of keys) out[key] = revive(record[key]);
    return out;
  }
  return value;
}

export type DancerLinkedBulletinRow = Record<string, unknown> & {
  id: string;
  title: string;
  audienceRoles: UserRole[];
  viaDancerLink: true;
};

function parseRow(value: unknown): DancerLinkedBulletinRow | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const row = revive(value) as Record<string, unknown>;
  if (typeof row.id !== 'string' || !row.id || typeof row.title !== 'string') return null;
  if (!Array.isArray(row.audienceRoles) || !row.audienceRoles.includes('family')) return null;
  return { ...row, viaDancerLink: true } as DancerLinkedBulletinRow;
}

interface ClientDeps {
  fetchImpl?: typeof fetch;
  headers?: typeof functionHeaders;
  nowMs?: number;
}

const cache = new Map<string, { at: number; promise: Promise<DancerLinkedBulletinRow[]> }>();

/**
 * Drop every shared answer, after a Got it through the lane. Answers are
 * keyed by uid, so a different person signing in on this tab never reads
 * another's; nothing needs clearing on sign-out.
 */
export function forgetDancerLinkedBulletins(): void {
  cache.clear();
}

/**
 * The family announcements this staff parent reads, newest first. Resolves
 * to [] for a staff member with no dancer link. Rejects on a lane failure,
 * so the caller can keep her own announcements on screen and say nothing.
 */
export function fetchDancerLinkedBulletins(
  user: User,
  max: number,
  deps: ClientDeps = {}
): Promise<DancerLinkedBulletinRow[]> {
  const nowMs = deps.nowMs ?? Date.now();
  const key = `${user.uid}:${max}`;
  const hit = cache.get(key);
  if (hit && nowMs - hit.at < STAFF_PARENT_BULLETINS_FRESH_MS) return hit.promise;
  const promise = (async () => {
    const response = await (deps.fetchImpl ?? fetch)(
      `${STAFF_PARENT_BULLETINS_ENDPOINT}?max=${encodeURIComponent(String(max))}`,
      {
        method: 'GET',
        headers: await (deps.headers ?? functionHeaders)(user, { json: false }),
        cache: 'no-store',
      }
    );
    const payload = (await response.json().catch(() => ({}))) as {
      linked?: unknown;
      bulletins?: unknown;
    };
    if (!response.ok || typeof payload.linked !== 'boolean' || !Array.isArray(payload.bulletins)) {
      throw new Error('Family announcements for your dancers could not be checked.');
    }
    const rows = payload.bulletins.map(parseRow);
    return rows.filter((row): row is DancerLinkedBulletinRow => row !== null);
  })();
  cache.set(key, { at: nowMs, promise });
  // A failure is not remembered: the next screen to mount asks again.
  promise.catch(() => {
    if (cache.get(key)?.promise === promise) cache.delete(key);
  });
  return promise;
}

function seenError(code: string | undefined): string {
  if (code === 'not_linked' || code === 'staff_parent_only') {
    return 'Your link to this dancer changed. Refresh and try again.';
  }
  if (code === 'not_found') return 'That announcement is no longer here.';
  if (code === 'too_many_requests') return 'Announcements are updating often. Wait a moment, then try again.';
  return 'That did not save. Try again.';
}

/** Got it on a family announcement she reads through her dancer. */
export async function markDancerLinkedBulletinSeen(
  user: User,
  bulletinId: string,
  deps: ClientDeps = {}
): Promise<void> {
  const response = await (deps.fetchImpl ?? fetch)(STAFF_PARENT_BULLETINS_ENDPOINT, {
    method: 'POST',
    headers: await (deps.headers ?? functionHeaders)(user),
    body: JSON.stringify({ bulletinId }),
  });
  const payload = (await response.json().catch(() => ({}))) as { ok?: boolean; error?: string };
  if (!response.ok || payload.ok !== true) throw new Error(seenError(payload.error));
  forgetDancerLinkedBulletins();
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(new Event(STAFF_PARENT_BULLETINS_CHANGED_EVENT));
  }
}

/**
 * B60: ask the lane to add the signed-in viewer to a still-current
 * must-acknowledge announcement she joined the audience of after it was
 * published (lib/bulletinLateRecipient.ts). The lane re-checks everything and
 * writes her uid into the recipient list only, so her Understood then goes
 * through the ordinary write. Never an alert. Never rejects: `retry` is true
 * only for passing trouble (network, 429, 5xx), so the caller asks again on a
 * later snapshot, and false on a refusal, so a refused caller never loops.
 */
export async function joinBulletinAsLateRecipient(
  user: User,
  bulletinId: string,
  deps: ClientDeps = {}
): Promise<{ joined: boolean; retry: boolean }> {
  let response: Response;
  try {
    response = await (deps.fetchImpl ?? fetch)(STAFF_PARENT_BULLETINS_ENDPOINT, {
      method: 'POST',
      headers: await (deps.headers ?? functionHeaders)(user),
      body: JSON.stringify({ bulletinId, joinAsLateRecipient: true }),
    });
  } catch {
    return { joined: false, retry: lateJoinMayRetry(null) };
  }
  const payload = (await response.json().catch(() => ({}))) as { ok?: boolean; joined?: boolean };
  if (response.ok && payload.ok === true) return { joined: payload.joined === true, retry: false };
  return { joined: false, retry: lateJoinMayRetry(response.ok ? 500 : response.status) };
}
